import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchQuotes } from "@/lib/api";
import { ArrowLeft, RefreshCw, Mail, Phone, Building2 } from "lucide-react";
import Logo from "@/components/Logo";

export default function Admin() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    fetchQuotes().then(setQuotes).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#F7F8FA] text-[#0F172A]">
      <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-medium text-[#475569] hover:text-[#00439C] transition" data-testid="admin-back-home">
              <ArrowLeft size={16} /> Back to site
            </Link>
            <div className="flex items-center gap-3 pl-6 border-l border-[#E2E8F0]">
              <Logo size="md" variant="dark" />
              <span className="text-[12px] uppercase tracking-widest font-semibold text-[#00439C]">Admin</span>
            </div>
          </div>
          <button
            onClick={load}
            data-testid="admin-refresh"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border border-[#E2E8F0] rounded-md hover:border-[#00439C] hover:text-[#00439C] transition bg-white"
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-12 lg:py-16">
        <div className="mb-10">
          <div className="text-[12px] uppercase tracking-widest font-semibold text-[#00439C] mb-2">Inbound briefs</div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-[#0F172A] tracking-tight">Quote requests</h1>
          <p className="mt-2 text-[#475569]">{quotes.length} {quotes.length === 1 ? "brief" : "briefs"} submitted to date.</p>
        </div>

        {loading ? (
          <div className="text-[#64748B]" data-testid="admin-loading">Loading…</div>
        ) : quotes.length === 0 ? (
          <div className="border border-[#E2E8F0] bg-white rounded-xl p-12 text-center" data-testid="admin-empty">
            <div className="text-[#0F172A] font-semibold mb-1">No briefs yet</div>
            <div className="text-sm text-[#64748B]">When prospects submit the quote form, they’ll appear here.</div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="admin-quotes-list">
            {quotes.map((q) => (
              <article key={q.id} data-testid={`admin-quote-${q.id}`} className="bg-white border border-[#E2E8F0] rounded-xl p-6 lg:p-7 hover:shadow-md transition">
                <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                  <div>
                    <h2 className="font-display font-bold text-lg text-[#0F172A]">{q.name}</h2>
                    {q.company && (
                      <div className="flex items-center gap-1.5 mt-1 text-sm text-[#64748B]">
                        <Building2 size={13} /> {q.company}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="inline-flex items-center px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider bg-[#EFF4FB] text-[#00439C] rounded">
                      {q.project_type}
                    </span>
                    <div className="text-xs text-[#64748B]">{new Date(q.created_at).toLocaleString()}</div>
                  </div>
                </div>
                <div className="grid sm:grid-cols-3 gap-4 mb-4 pb-4 border-b border-[#F1F3F7]">
                  <Meta icon={Mail} label="Email" value={q.email} />
                  <Meta icon={Phone} label="Phone" value={q.phone || "—"} />
                  <Meta label="Company" value={q.company || "—"} />
                </div>
                <p className="text-[#1E293B] text-sm leading-relaxed">{q.message}</p>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

const Meta = ({ icon: Icon, label, value }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wider font-semibold text-[#64748B] mb-1 inline-flex items-center gap-1.5">
      {Icon && <Icon size={12} />} {label}
    </div>
    <div className="text-sm text-[#0F172A] break-words">{value}</div>
  </div>
);
